import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import { h, type VNode } from 'vue';
import type { Element, Root, RootContent, Text } from 'hast';
import type { HtmlToVNodesReplacers, HtmlVNodeChildren, ReplaceResult } from './types';

export type * from './types';

const parser = unified().use(rehypeParse, { fragment: true });

/**
 * Преобразует hast-свойства в атрибуты для Vue `h()`.
 * hast хранит className как массив, data-/aria- атрибуты в camelCase —
 * приводим к формату, который понимает Vue.
 */
export function hastPropertiesToAttrs(
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const attrs: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(properties ?? {})) {
    if (val == null || val === false) continue;

    if (key === 'className') {
      attrs.class = Array.isArray(val) ? val.join(' ') : val;
    } else if (/^(data|aria)[A-Z]/.test(key)) {
      // dataModal → data-modal, ariaLabel → aria-label
      attrs[key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)] =
        val === true ? '' : val;
    } else {
      attrs[key] = val === true ? '' : val;
    }
  }

  return attrs;
}

function toChildren(result: Exclude<ReplaceResult, undefined>): HtmlVNodeChildren {
  return Array.isArray(result) ? result : [result];
}

/**
 * Raw-text элементы: браузер не декодирует внутри них HTML-сущности.
 * Отдавать их содержимое обычными текстовыми детьми нельзя — Vue SSR
 * экранирует текст, и в разметку уходит `content: &quot;…&quot;`
 * (сломанный CSS + hydration text mismatch).
 */
const RAW_TEXT_TAGS = new Set(['style', 'script']);

/** Рекурсивно обходит hast-дерево и строит массив Vue VNode. */
function convert(
  node: RootContent | Root,
  replacers: HtmlToVNodesReplacers,
  parent?: Element | Root,
): HtmlVNodeChildren {
  if (node.type === 'text') {
    const text = (node as Text).value;
    const replaced = replacers.text?.(text);
    return replaced === undefined ? [text] : toChildren(replaced);
  }

  if (node.type === 'element') {
    const el = node as Element;
    const renderChildren = () =>
      el.children.flatMap((child) => convert(child, replacers, el));
    const replaced = replacers.element?.(el, renderChildren, parent);
    if (replaced !== undefined) return toChildren(replaced);

    if (RAW_TEXT_TAGS.has(el.tagName)) {
      const rawText = el.children
        .map((child) => (child.type === 'text' ? child.value : ''))
        .join('');

      return [
        h(el.tagName, {
          ...hastPropertiesToAttrs(el.properties),
          innerHTML: rawText,
        }),
      ];
    }

    const children = renderChildren();
    return [
      h(
        el.tagName,
        hastPropertiesToAttrs(el.properties),
        children.length ? children : undefined,
      ),
    ];
  }

  if (node.type === 'root') {
    const root = node as Root;
    return root.children.flatMap((child) => convert(child, replacers, root));
  }

  // comment, doctype — пропускаем
  return [];
}

/**
 * Парсит HTML-строку (rehype) и конвертирует в массив Vue VNode.
 * Через `replacers` на место любых элементов/подстрок текста можно
 * подставить настоящие Vue-компоненты — так формы и видео-плееры
 * встраиваются прямо в CMS-контент, SSR-friendly (в отличие от v-html).
 *
 * Готовые применения: `shared/ui/cms-content` (тексты статей,
 * iframe-плееры → LazyIframeVideo) и `widgets/landing-page`
 * (шорткоды форм → FormPersConsultation).
 */
export function htmlToVNodes(
  html: string,
  replacers: HtmlToVNodesReplacers = {},
): (string | VNode)[] {
  if (!html) return [];
  return convert(parser.parse(html), replacers);
}
